import i18n from '@/i18n';

export type FormFieldDescriptor = {
  elementId: string;
  name: string;
  priority: number;
};

export type FormFieldError = {
  id: string;
  errorMessage: string | undefined;
};

/**
 * Smoothly scrolls the element with the given id into view (centered).
 * No-op if the element does not exist.
 */
export function scrollToElement(elementId: string): void {
  const element = document.getElementById(elementId);
  if (!element) return;

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
}

/**
 * Looks each error up in the supplied fields list and returns the
 * highest-priority match (lowest priority number wins). Errors not present
 * in the fields list are ignored.
 */
export function findPriorityError(
  errors: FormFieldError[],
  fields: FormFieldDescriptor[],
): (FormFieldDescriptor & { errorMessage: string | undefined }) | null {
  const matched = errors
    .map((error) => {
      const lookup = fields.find((field) => field.elementId === error.id);
      if (!lookup) return null;
      return {
        ...lookup,
        errorMessage: error.errorMessage,
      };
    })
    .filter(
      (entry): entry is FormFieldDescriptor & { errorMessage: string | undefined } => (
        !!entry && !!entry.errorMessage
      ),
    );

  matched.sort((a, b) => a.priority - b.priority);
  return matched[0] || null;
}

/**
 * Scrolls to the highest-priority invalid input from `errors` and returns
 * the localized "{name} is required" message for that field. Returns null
 * if no matching error is found.
 */
export function validateAndScrollToError(
  errors: FormFieldError[],
  fields: FormFieldDescriptor[],
): string | null {
  const priorityError = findPriorityError(errors, fields);
  if (!priorityError) return null;

  scrollToElement(priorityError.elementId);
  return i18n.t(
    'shipment.validationErrors.requiredPriorityField',
    { name: priorityError.name },
  ) as string;
}
